import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { PropertyOption } from '../../types';
import type { FiltersListItemProps } from './filters-list-item/filters-list-item';
import { FiltersListItem } from './filters-list-item/filters-list-item';
import { FiltersListHeader } from './filters-list-header/filters-list-header';
import { AddFilterButton } from './add-filter-button/add-filter-button';
import styles from './filters-list.module.css';

interface FiltersListProps {
  availableFilters: PropertyOption[];
  pinnedFilters: string[];
  activeFilters?: string[];
  onFilterSelect?: (filter: PropertyOption) => void;
  children: (filter: PropertyOption, itemProps: FiltersListItemProps) => ReactNode;
}

function FiltersListComponent({
  availableFilters,
  pinnedFilters,
  activeFilters = [],
  onFilterSelect,
  children,
}: FiltersListProps) {
  const [pendingFilter, setPendingFilter] = useState<PropertyOption | null>(null);
  const [activePinnedFilters, setActivePinnedFilters] = useState<string[]>(
    activeFilters.filter(key => pinnedFilters.includes(key))
  );

  useEffect(() => {
    setActivePinnedFilters(prev => {
      return prev.filter(f => activeFilters.includes(f));
    });
  }, [activeFilters]);

  const shownFilterKeys = new Set([...activeFilters, ...pinnedFilters]);
  const activeFiltersToRender = activeFilters.filter(
    key => !pinnedFilters.includes(key) && pendingFilter?.key !== key
  );

  return (
    <div className={styles.container}>
      {[...activeFiltersToRender, ...activePinnedFilters].flatMap(key => {
        const filter = availableFilters.find(f => f.key === key);
        if (!filter) return [];

        return [<Fragment key={filter.key}>{children(filter, {})}</Fragment>];
      })}

      {pinnedFilters
        .filter(key => !activePinnedFilters.includes(key))
        .flatMap(key => {
          const filter = availableFilters.find(f => f.key === key);
          return filter
            ? [
                <Fragment key={filter.key}>
                  {children(filter, {
                    onClose: () => {
                      if (activeFilters.includes(key)) {
                        setActivePinnedFilters(prev => [...prev, key]);
                      } else {
                        setActivePinnedFilters(prev => prev.filter(k => k !== key));
                      }
                    },
                  })}
                </Fragment>,
              ]
            : [];
        })}

      {pendingFilter
        ? children(pendingFilter, {
            autoOpen: true,
            onClose: () => {
              setPendingFilter(null);
            },
          })
        : null}

      <AddFilterButton
        onSelect={key => {
          const selectedFilter = availableFilters.find(f => f.key === key);
          setPendingFilter(selectedFilter ?? null);
          if (selectedFilter) {
            onFilterSelect?.(selectedFilter);
          }
        }}
        options={availableFilters
          .filter(filter => !shownFilterKeys.has(filter.key))
          .map(filter => ({
            label: filter.label,
            value: filter.key,
          }))}
      />
    </div>
  );
}

export const FiltersList = Object.assign(FiltersListComponent, {
  Header: FiltersListHeader,
  ListItem: FiltersListItem,
});
